import React, { FC, MutableRefObject } from 'react';
import { Alert as DefaultAlert, Button as DefaultButton, Loading as DefaultLoading } from 'framework/ui/mui-builder';

import RefreshIcon from '@mui/icons-material/RefreshOutlined';
import classnames from 'classnames';
import { useHeight } from 'framework/utils';
import { useCollapsibleHelper, CollapsibleProps, DEFAULT_MAX_HEIGHT } from '../../field';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useUiTheme } from 'framework/uiTheme';
import Box from '@mui/material/Box';

const TimelineCollapsible: FC<CollapsibleProps> = (props: CollapsibleProps) => {
  const { Alert: ThemedAlert, Button: ThemedButton, Loading: ThemedLoading } = useUiTheme();
  const Alert = ThemedAlert || DefaultAlert;
  const Button = ThemedButton || DefaultButton;
  const Loading = ThemedLoading || DefaultLoading;
  const { children, maxHeight = DEFAULT_MAX_HEIGHT, getFullText } = props;
  const { ref, height } = useHeight();
  const {
    isCollapsible,
    isExpandableField,
    isExpanded,
    displayedValue,
    isLoading,
    toggleBlock,
    retryUploadData,
    error,
  } = useCollapsibleHelper({
    ...props,
    height,
  });
  const isBlockExpanded = isExpanded || !isCollapsible;

  const renderText = () => {
    if (isLoading) {
      return <Loading />;
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 0 }}>
          <div>{error}</div>
          <Button variant="text" onClick={retryUploadData} startIcon={<RefreshIcon />} />
        </Alert>
      );
    }

    return (
      <div ref={ref as MutableRefObject<Nullable<HTMLDivElement>>}>
        {React.cloneElement(children, {
          value: displayedValue,
        })}
      </div>
    );
  };

  const determineMaxHeight = () => {
    if (isBlockExpanded || isExpandableField) {
      return getFullText ? height : 'inherit';
    }
    return maxHeight;
  };

  return (
    <>
      <Box
        className={classnames('timeline-collapsible', { 'show-blur': !isBlockExpanded })}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          maxHeight: determineMaxHeight(),
          height: isLoading ? maxHeight : 'inherit',
          transition: 'max-height 0.2s',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            zIndex: !isBlockExpanded ? 'inherit' : -1,
            display: 'block',
            width: '100%',
            height: '50px',
            opacity: !isBlockExpanded ? 0.9 : 0,
            backgroundImage: theme =>
              `linear-gradient(0, ${theme.palette.common.white}, ${theme.palette.common.transparentWhite})`,
            transition: 'opacity 0.3s',
          },
        }}
      >
        {renderText()}
      </Box>
      {isCollapsible && (
        <Button
          type="button"
          className="timeline-collapsible-button"
          size="small"
          onClick={toggleBlock}
          disabled={isLoading}
          sx={{
            display: 'flex',
            width: '100%',
            maxHeight: '12px',
            mt: isBlockExpanded && !isLoading ? 2 : 1,
            mb: 3,
            backgroundColor: 'border.medium',
            '& svg': { fontSize: '16px' },
          }}
        >
          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </Button>
      )}
    </>
  );
};

export default TimelineCollapsible;
